import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { isIOS, isStandalone } from './platform';

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const isPushSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

// Unsubscribes this device from push: browser-side first (that's the part
// that actually stops notifications and doesn't depend on the network),
// then best-effort tells the server. Exported standalone, not just used by
// the toggle below -- also called from the logout action (see
// actions/registerUser.js), since a signed-out device shouldn't keep
// receiving push for an account it's no longer in. getRegistration()
// rather than .ready deliberately -- this needs to resolve (as a no-op)
// rather than hang if there's no active service worker for whatever
// reason, since callers treat this as fire-and-forget.
export const unsubscribeFromPush = async () => {
  if (!('serviceWorker' in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration || !registration.pushManager) return;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  const { endpoint } = subscription;
  await subscription.unsubscribe();
  try {
    await axios.delete('/api/users/push/subscribe', { data: { endpoint } });
  } catch (err) {
    console.error('Failed to remove subscription from server:', err.message);
  }
};

// Wraps subscribing/unsubscribing to web push behind a single toggle().
// Permission is only ever requested from inside it -- never on mount --
// since browsers block or auto-deny a permission prompt that isn't
// triggered by a user gesture, and asking unprompted on page load is
// hostile regardless.
//
// iOS only delivers web push to an installed (Add to Home Screen) PWA --
// plain Safari has no PushManager at all, even on iOS versions that support
// the API once installed. needsIOSInstall flags that case so the caller can
// show an explanation instead of a toggle that can never work.
const usePushNotifications = (enabled) => {
  const [permission, setPermission] = useState(() =>
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const supported = isPushSupported();
  const needsIOSInstall = isIOS() && !isStandalone();
  const canShowToggle = enabled && supported && !needsIOSInstall;

  useEffect(() => {
    if (!canShowToggle) return undefined;
    let cancelled = false;
    navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((sub) => {
        if (!cancelled) setSubscribed(!!sub);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [canShowToggle]);

  const subscribe = async () => {
    const permissionResult = await Notification.requestPermission();
    setPermission(permissionResult);
    if (permissionResult !== 'granted') {
      setSubscribed(false);
      return;
    }

    const { data } = await axios.get('/api/users/push/vapid-public-key');
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });

    try {
      const json = subscription.toJSON();
      await axios.post('/api/users/push/subscribe', { endpoint: json.endpoint, keys: json.keys });
    } catch (err) {
      // The browser now has a live subscription the server never heard
      // about -- left alone, pushManager.getSubscription() would keep
      // reporting "subscribed" on every future visit even though no push
      // will ever arrive for it (the server has no record to send to).
      // Undo the browser side so state stays truthful.
      console.error('Failed to save subscription on server:', err.message);
      await subscription.unsubscribe().catch(() => {});
      setSubscribed(false);
    }
  };

  // Moves the switch immediately, then reconciles with what actually
  // happened. A failure anywhere in the real subscribe/unsubscribe work
  // (as opposed to the best-effort server-sync steps above, which swallow
  // their own errors) reverts the switch to where it started, so it can
  // never end up showing something the browser's real subscription state
  // disagrees with.
  const toggle = useCallback(async () => {
    if (busy || permission === 'denied') return;
    const goingOn = !subscribed;
    setSubscribed(goingOn);
    setBusy(true);
    try {
      if (goingOn) await subscribe();
      else await unsubscribeFromPush();
    } catch (err) {
      console.error('Push toggle failed:', err.message);
      setSubscribed(!goingOn);
    } finally {
      setBusy(false);
    }
  }, [busy, subscribed, permission]);

  return {
    canShowToggle,
    permission,
    subscribed,
    busy,
    toggle,
  };
};

export default usePushNotifications;
